import type { StepStatus } from '@/lib/journey'
import { boardStatusOf, compareBoardCards, matchesBoardStatus, type BoardStatus } from '@/lib/board/boardCardView'
import type { BoardStatusFilter } from '@/lib/board/boardFilters'
import { deriveProcedureStepStatus, type KitProgress } from '@/lib/procedureStepStatus'
import type { Procedure, ProcedureType } from '@/types/cgt'

/** A run that is neither finished nor cancelled reads Expired once its cleaning day has passed. */
function expiredOrNot(status: BoardStatus, expired: boolean | undefined): BoardStatus {
  return expired && status !== 'complete' && status !== 'cancelled' ? 'expired' : status
}

/** One card on the Overall Status board. */
export interface BoardRun {
  procedure: Procedure
  type: ProcedureType
  patientName: string | null
  completed: number
  total: number
  stepStatus: StepStatus
  stepLabel: string
  boardStatus: BoardStatus
}

/**
 * The board's cards: each run's status derived the same way the procedure
 * tables derive it, filtered by the status filter, in board order.
 *
 * A kit run takes its status from its build, so its kit row is used when one
 * is known; progress for every other run comes from the list summary.
 */
export function buildBoardRuns(
  procedures: Procedure[],
  ctx: {
    nameByMrn: Map<string, string>
    kitByProcedureId: Map<string, KitProgress>
    status: BoardStatusFilter
    /** Cleaning runs whose task's day has passed undone (z8nrz7f9u5). */
    expiredRunIds?: ReadonlySet<string>
  },
): BoardRun[] {
  return procedures
    .map(procedure => {
      const type = procedure.procedureTemplate.type ?? 'Transplant'
      const progress = procedure.summary?.progress
      const derived = deriveProcedureStepStatus({
        procedureStatus: procedure.status,
        completed: progress?.completed ?? 0,
        total: progress?.total ?? 0,
        hasActive: progress?.hasActive ?? false,
        hasPendingReview: procedure.hasPendingReview ?? false,
        kit: type === 'KitCreation' ? ctx.kitByProcedureId.get(procedure.id) : undefined,
      })
      return {
        procedure,
        type,
        // A kit run's patientMrn is its supply id, never a patient.
        patientName:
          type !== 'KitCreation' && procedure.patientMrn ? (ctx.nameByMrn.get(procedure.patientMrn) ?? null) : null,
        ...derived,
        boardStatus: expiredOrNot(boardStatusOf(procedure.status, derived.stepStatus), ctx.expiredRunIds?.has(procedure.id)),
      }
    })
    .filter(run => matchesBoardStatus(run.boardStatus, ctx.status))
    .sort((a, b) => compareBoardCards(a.procedure, b.procedure))
}
